import { loadRemoteModule } from '@angular-architects/native-federation';

// Remote entry locations. These were previously listed in
// assets/federation.manifest.json and fetched + registered EAGERLY in main.ts,
// which put one manifest fetch and one remoteEntry.json fetch PER REMOTE on the
// boot critical path (blank screen until all of them resolved — the Datadog
// ">15s initial load" alerts). The paths are same-origin/relative by design
// (identical in dev via proxy.conf.js and in prod under one CloudFront origin,
// see scripts/serve-mfe-static.js), so they can live here as constants and each
// remote is registered on FIRST NAVIGATION instead of at boot.
//
// loadRemoteModule({ remoteEntry, ... }) fetches and registers a remote exactly
// once (the runtime keeps a per-entry initialized registry); later navigations
// reuse the registration. If the fetch fails, the router surfaces a navigation
// error handled by the existing stale-chunk/navigation error recovery — the
// same behavior a failed lazy chunk load has today.
const REMOTE_ENTRIES = {
  admin: '/admin-remote/remoteEntry.json',
  reports: '/reports-remote/remoteEntry.json',
  emails: '/emails-remote/remoteEntry.json',
  'stacking-plan': '/stacking-plan-remote/remoteEntry.json',
} as const;

export type MiqRemoteName = keyof typeof REMOTE_ENTRIES;

/**
 * Phase D: prod builds inline ONE merged native import map (see
 * scripts/build-native-importmap.js) that already contains every remote's
 * exposes ("admin/Routes" -> /admin-remote/Routes-XXXX.js) and per-remote
 * scopes for its shared deps. On those builds a remote load is a plain native
 * dynamic import — no remoteEntry.json fetch, no NF runtime, and critically no
 * importShim (the NF runtime prefers window.importShim whenever es-module-shims
 * is present, which would re-route the load through the shim pipeline).
 * Dev serve has no inline map (the NF builder keeps shim mode), so it falls
 * back to loadRemoteModule exactly as before. Detection is memoized: the map
 * is static build-time markup, so its presence cannot change at runtime.
 */
let nativeMap: boolean | undefined;
function hasNativeFederationMap(): boolean {
  nativeMap ??= document.querySelector('script[type="importmap"][data-miq-federation]') !== null;
  return nativeMap;
}

/** Load an exposed module from a federation remote, registering the remote on first use. */
export function loadMiqRemote<T = any>(remoteName: MiqRemoteName, exposedModule: string): Promise<T> {
  if (hasNativeFederationMap()) {
    // "./Routes" -> "admin/Routes", matching the specifiers the build step
    // writes into the inline map (same scheme the NF runtime's joinPaths used).
    const specifier = `${remoteName}/${exposedModule.replace(/^\.\//, '')}`;
    return import(/* @vite-ignore */ specifier) as Promise<T>;
  }
  return loadRemoteModule<T>({
    remoteName,
    remoteEntry: REMOTE_ENTRIES[remoteName],
    exposedModule,
  });
}
